import { useAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { CarouselItem, ErsteMarketResource, InstrumentResource } from '../../resources';
import { ersteMarketContentAtom, ersteMarketPositionTippsAtom } from '../../state';
import { Content } from '../../types';
import { useInstrumentSearchCommon, UseInstrumentSearchTranslation } from '../instrument';
import { GetResourceHeader, GetResourceHeaderWithToken } from '../useResource';

export type ErsteMarketCommonProps = {
  ersteMarketResource: ErsteMarketResource;
  getResourceHeader: GetResourceHeader;
  getResourceHeaderWithToken: GetResourceHeaderWithToken;
  instrumentResource: InstrumentResource;
  translation: UseInstrumentSearchTranslation;
};

export const useErsteMarketCommon = ({
  ersteMarketResource,
  getResourceHeader,
  getResourceHeaderWithToken,
  instrumentResource,
  translation,
}: ErsteMarketCommonProps) => {
  const { getListedInstrumentList } = useInstrumentSearchCommon({
    getResourceHeaderWithToken,
    instrumentResource,
    translation,
  });
  const [positionTipps, setPositionTipps] = useAtom(ersteMarketPositionTippsAtom);
  const [contents, setContents] = useAtom(ersteMarketContentAtom);

  const isPositionsLoaded = useRef(false);
  const isContentsLoaded = useRef(false);

  const loadPositionTipps = useCallback(async () => {
    if (isPositionsLoaded.current) {
      return;
    }

    setPositionTipps(prev => ({
      ...prev,
      error: false,
      isLoading: true,
    }));
    try {
      const result = await ersteMarketResource.getPositionTipps(getResourceHeader());
      const instrumentRicList = result.data.map(item => item.ric);
      const instrumentList = await getListedInstrumentList(instrumentRicList);

      setPositionTipps({
        isLoading: false,
        error: false,
        tipps: result.data.map(item => ({
          ...item,
          instrument: instrumentList?.find(instrument => instrument.ric === item.ric)!,
        })),
      });
      isPositionsLoaded.current = true;
    } catch (e) {
      setPositionTipps({
        error: true,
        tipps: [],
        isLoading: false,
      });
    }
  }, [ersteMarketResource, getResourceHeader, setPositionTipps, getListedInstrumentList]);

  const loadContents = useCallback(async () => {
    if (isContentsLoaded.current) {
      return;
    }

    setContents(prev => ({
      ...prev,
      isLoading: true,
      error: false,
    }));
    try {
      const result = await ersteMarketResource.getContent(getResourceHeader());
      const assignedColorsForPromo = new Map();
      const possibleLightColors = ['ffffff', 'e9eff9', 'dce4ea'];
      const replacementColors = ['#742770', '#2870ed', '#3e9d9d'];

      const getRandomColor = () => replacementColors[Math.floor(Math.random() * replacementColors.length)];

      const getBackgroundColor = (item: CarouselItem) => {
        if (item.background && !possibleLightColors.includes(item.background?.toLowerCase())) {
          return `#${item.background}`;
        }

        if (item.background && possibleLightColors.includes(item.background?.toLowerCase())) {
          if (!assignedColorsForPromo.has(item)) {
            assignedColorsForPromo.set(item, getRandomColor());
          }
          return assignedColorsForPromo.get(item);
        }

        return 'var(--primary)';
      };

      const promoWithColors = result.data
        .filter((item: Content) => item.categoryId !== 'banner')
        .map((promoItem: Content) => ({
          ...promoItem,
          background: getBackgroundColor(promoItem),
        }));

      setContents({
        isLoading: false,
        error: false,
        promo: promoWithColors.slice(0, 3),
        tops: result.data.filter((item: Content) => item.categoryId === 'banner').slice(0, 3),
      });
      isContentsLoaded.current = true;
    } catch (e) {
      setContents({
        isLoading: false,
        error: true,
        promo: [],
        tops: [],
      });
    }
  }, [ersteMarketResource, getResourceHeader, setContents]);

  return { loadContents, loadPositionTipps, positionTipps, contents };
};
